import React from 'react';

import { useSearchParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useGetCompetitorPropertySnapshot } from '~/hooks/apis/price-parity/useGetCompetitorPropertySnapshot';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import CompetitorOfferContents from './CompetitorOfferContents';
import CompetitorOfferHistoryTable from './CompetitorOfferHistoryTable';
import CompetitorOfferLeOffersTable from './CompetitorOfferLeOffersTable';
import CompetitorOfferOptionsTable from './CompetitorOfferOptionsTable';
import CompetitorOfferPackageComparison from './CompetitorOfferPackageComparison';
import CompetitorOfferSiteLink from './CompetitorOfferSiteLink';
import CompetitorOfferStatusChips from './CompetitorOfferStatusChips';
import CompetitorOfferSummary from './CompetitorOfferSummary';
import CompetitorOfferTimeline from './CompetitorOfferTimeline';
import { DATE_TIME_FORMAT } from './constants';
import { withSnapshot } from './utils';

const SECTION_HEADING_SX = { fontSize: 18, fontWeight: 500, mb: 1.5 };
const SECTION_SX = { scrollMarginTop: 16 };
// The past collection on show, so a snapshot can be shared.
const COLLECTION_PARAM = 'collection';

enum Section {
  SUMMARY = 'summary',
  FLASH_OFFERS = 'flash-offers',
  PACKAGE_COMPARISON = 'package-comparison',
  ROOM_OPTIONS = 'room-options',
  TIMELINE = 'timeline',
  HISTORY = 'collection-history',
}

const SECTION_TITLES: Record<Section, string> = {
  [Section.SUMMARY]: 'Summary',
  [Section.FLASH_OFFERS]: 'Flash offers',
  [Section.PACKAGE_COMPARISON]: 'Package comparison',
  [Section.ROOM_OPTIONS]: 'Room options',
  [Section.TIMELINE]: 'Timeline',
  [Section.HISTORY]: 'Collection history',
};

const CONTENTS = Object.values(Section).map((id) => ({ id, title: SECTION_TITLES[id] }));

interface Props {
  property: CompetitorProperty;
}

// One competitor listing: what the latest (or a chosen past) collection saw, then its timeline and history.
export default function CompetitorOfferSupplierPanel({ property }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const collectionId = searchParams.get(COLLECTION_PARAM) ?? undefined;
  const snapshot = useGetCompetitorPropertySnapshot(property.sourceName, property.externalPropertyId, collectionId);
  const shown = snapshot.data ? withSnapshot(property, snapshot.data) : property;
  const collectedAt = collectionId ? snapshot.data?.collectedAt : property.lastCollectedAt;
  // While a past collection loads (or failed to), show nothing rather than the latest data under its banner.
  const awaitingSnapshot = !!collectionId && !snapshot.data;

  const showCollection = (nextCollectionId?: string) => {
    setSearchParams(nextCollectionId ? { [COLLECTION_PARAM]: nextCollectionId } : {});
    document.getElementById(Section.SUMMARY)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Stack direction="row" spacing={4}>
      <Stack spacing={3.5} flex={1} minWidth={0}>
        {collectionId && (
          <Alert
            severity={snapshot.error ? 'warning' : 'info'}
            action={
              <Button color="inherit" size="small" onClick={() => showCollection()}>
                Back to latest
              </Button>
            }
          >
            {snapshot.isLoading && 'Loading the past collection…'}
            {snapshot.error && `Couldn't load this collection: ${snapshot.error.message}`}
            {snapshot.data &&
              `Viewing the collection of ${collectedAt ? dayjs(collectedAt).format(DATE_TIME_FORMAT) : 'an unknown date'}. Summary, package comparison and room options show what it captured.`}
          </Alert>
        )}

        <Box component="section" id={Section.SUMMARY} sx={SECTION_SX}>
          <Stack direction="row" alignItems="baseline" spacing={1.5} mb={1.5}>
            <Typography variant="h6" component="h2" sx={{ ...SECTION_HEADING_SX, mb: 0 }}>
              {SECTION_TITLES[Section.SUMMARY]}
            </Typography>
            {!awaitingSnapshot && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {collectedAt ? `Collected ${dayjs(collectedAt).format(DATE_TIME_FORMAT)}` : 'Never collected'}
                </Typography>
                {!collectionId && <CompetitorOfferStatusChips property={property} />}
                <CompetitorOfferSiteLink property={shown} />
              </>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {collectionId
              ? 'From a past collection. The deal and prices may have changed since.'
              : 'From the latest collection that found this listing. Availability and terms may have changed since.'}
          </Typography>
          {snapshot.isLoading && <CircularProgress size={24} />}
          {!awaitingSnapshot && <CompetitorOfferSummary property={shown} />}
        </Box>

        <Box component="section" id={Section.FLASH_OFFERS} sx={SECTION_SX}>
          <Typography variant="h6" component="h2" sx={SECTION_HEADING_SX}>
            {SECTION_TITLES[Section.FLASH_OFFERS]}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Our Flash offers on the matched properties, from the Luxury Escapes availability schedule. Drafts are left
            out.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, overflowX: 'auto', borderRadius: 2 }}>
            <CompetitorOfferLeOffersTable property={property} />
          </Paper>
        </Box>

        <Box component="section" id={Section.PACKAGE_COMPARISON} sx={SECTION_SX}>
          <Typography variant="h6" component="h2" sx={SECTION_HEADING_SX}>
            {SECTION_TITLES[Section.PACKAGE_COMPARISON]}
          </Typography>
          <CompetitorOfferPackageComparison property={property} collectionId={collectionId} />
        </Box>

        <Box component="section" id={Section.ROOM_OPTIONS} sx={SECTION_SX}>
          <Typography variant="h6" component="h2" sx={SECTION_HEADING_SX}>
            {SECTION_TITLES[Section.ROOM_OPTIONS]}
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, overflowX: 'auto', borderRadius: 2 }}>
            <CompetitorOfferOptionsTable property={property} collectionId={collectionId} />
          </Paper>
        </Box>

        <Box component="section" id={Section.TIMELINE} sx={SECTION_SX}>
          <Typography variant="h6" component="h2" sx={SECTION_HEADING_SX}>
            {SECTION_TITLES[Section.TIMELINE]}
          </Typography>
          <CompetitorOfferTimeline
            suppliers={[property]}
            selectedCollectionId={collectionId}
            onSelectCollection={showCollection}
          />
        </Box>

        <Box component="section" id={Section.HISTORY} sx={SECTION_SX}>
          <Typography variant="h6" component="h2" sx={SECTION_HEADING_SX}>
            {SECTION_TITLES[Section.HISTORY]}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Every collection since the competitor first listed the hotel. Not found in a full collection usually means
            the deal ended or was hidden that day. Tick a collection to see what it captured.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, overflowX: 'auto', borderRadius: 2 }}>
            <CompetitorOfferHistoryTable
              property={property}
              selectedCollectionId={collectionId}
              onSelectCollection={showCollection}
            />
          </Paper>
        </Box>
      </Stack>
      <CompetitorOfferContents sections={CONTENTS} />
    </Stack>
  );
}
